import { HttpAgent } from '@ag-ui/client';
import { EventSchemas } from '@ag-ui/core';
import type { ChatAdapter } from '@mui/x-chat/headless';
import type { ChatMessage, ChatMessageChunk } from '@mui/x-chat/types';
import * as uuid from 'uuid';

import { api, isDefined } from 'common';

import { getAgenticChatRunError } from './runError';
import { createAgenticChatRunTranslator } from './translateRunEvents';

const AGENTIC_CHAT_RUN_PATH = '/agentic-platform/ag-ui/run';

function getMessageText(message: ChatMessage): string {
  return message.parts
    .map((part) => (part.type === 'text' ? part.text : ''))
    .join('')
    .trim();
}

function createAgenticChatRunStream(options: {
  agent: HttpAgent;
  signal: AbortSignal;
}): ReadableStream<ChatMessageChunk> {
  const assistantMessageId = uuid.v7();
  const translator = createAgenticChatRunTranslator({ assistantMessageId });
  const runAbortController = new AbortController();
  let isSettled = false;

  return new ReadableStream<ChatMessageChunk>({
    cancel() {
      isSettled = true;
      runAbortController.abort();
    },
    start(controller) {
      const abortRun = () => runAbortController.abort();
      options.signal.addEventListener('abort', abortRun, { once: true });

      const settle = (settleRun: () => void) => {
        if (isSettled) {
          return;
        }
        isSettled = true;
        settleRun();
      };

      void options.agent
        .runAgent(
          { abortController: runAbortController, runId: uuid.v7() },
          {
            onEvent: ({ event }) => {
              if (isSettled) {
                return;
              }
              for (const chunk of translator.getChunks(EventSchemas.parse(event))) {
                controller.enqueue(chunk);
              }

              const runErrorMessage = translator.getRunErrorMessage();
              if (isDefined(runErrorMessage)) {
                settle(() => {
                  runAbortController.abort();
                  controller.error(new Error(runErrorMessage));
                });
              }
            },
          }
        )
        .then(() => {
          // The protocol reports agent failures in-band on a 200 stream, so a resolved run can
          // still have failed.
          const runErrorMessage = translator.getRunErrorMessage();
          settle(() => {
            if (isDefined(runErrorMessage)) {
              controller.error(new Error(runErrorMessage));
              return;
            }
            controller.close();
          });
        })
        .catch((error: unknown) => {
          settle(() => {
            if (options.signal.aborted) {
              controller.enqueue({ messageId: assistantMessageId, type: 'abort' });
              controller.close();
              return;
            }
            controller.error(getAgenticChatRunError(error));
          });
        })
        .finally(() => {
          options.signal.removeEventListener('abort', abortRun);
        });
    },
  });
}

/**
 * Drives the agentic platform over AG-UI.
 *
 * Conversation history is not sent: the conversation id is the AG-UI thread id, and the platform
 * resumes the durable session behind it, so only the new user message travels with each run. Each
 * run therefore gets its own client: a reused one would carry the messages it assembled from the
 * previous run's events, including reasoning messages the platform does not accept back.
 *
 * `regenerate` is deliberately unimplemented. The platform has no notion of replacing a reply, so
 * the runtime's fallback of re-sending the anchoring user message is the honest behaviour: the
 * agent is asked again, with the previous exchange still in its session.
 */
export function createAgenticChatAdapter(): ChatAdapter {
  return {
    sendMessage: (input) => {
      const { conversationId, message, signal } = input;
      if (!isDefined(conversationId)) {
        return Promise.reject(new Error('An agentic chat run requires a conversation.'));
      }
      const agent = new HttpAgent({
        initialMessages: [{ content: getMessageText(message), id: message.id, role: 'user' }],
        threadId: conversationId,
        url: api.client.common.constructPath(AGENTIC_CHAT_RUN_PATH),
      });
      return Promise.resolve(createAgenticChatRunStream({ agent, signal }));
    },
  };
}
